import axios from 'axios'
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, SlidersHorizontal } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { getNotifications, getNotificationSummary, getRunningNotifications } from '@/api/services/notification'
import type { NotificationLogRead } from '@/api/types/notification'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { parseUtcDate } from '@/lib/date-utils'
import { getDomainLabel } from '@/lib/domain-labels'
import { useNotificationFilterStore } from '@/store/useNotificationFilterStore'

import { getDomainColor } from './domain-colors'
import { DomainFilterSheet } from './domain-filter-sheet'

function SortIcon({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
  if (!active) return <ArrowUpDown className="inline size-3 ml-1 opacity-30" />
  return direction === 'asc' ? <ArrowUp className="inline size-3 ml-1" /> : <ArrowDown className="inline size-3 ml-1" />
}

const formatDate = (iso: string) => {
  const d = parseUtcDate(iso)
  return (
    d.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
  )
}

/**
 * ドメインごとに「今日から何日連続でNGがあるか」を算出する。
 */
function calcConsecutiveNgDays(logs: NotificationLogRead[]): Record<string, number> {
  const domainNgDates = new Map<string, Set<string>>()
  for (const log of logs) {
    if (log.failed_count <= 0) continue
    const dateStr = parseUtcDate(log.created_at).toLocaleDateString('ja-JP')
    if (!domainNgDates.has(log.domain)) domainNgDates.set(log.domain, new Set())
    domainNgDates.get(log.domain)!.add(dateStr)
  }

  const result: Record<string, number> = {}
  const today = new Date()

  for (const [domain, dates] of domainNgDates) {
    let streak = 0
    const cursor = new Date(today)
    for (let i = 0; i < 365; i++) {
      const key = cursor.toLocaleDateString('ja-JP')
      if (dates.has(key)) {
        streak++
        cursor.setDate(cursor.getDate() - 1)
      } else {
        break
      }
    }
    result[domain] = streak
  }

  return result
}

export function NotificationLogTab() {
  const [logs, setLogs] = useState<NotificationLogRead[]>([])
  // NG連続日数計算はフィルタに影響されない全体データを使う (別途フィルタなしで取得)。
  const [ngStreaksLogs, setNgStreaksLogs] = useState<NotificationLogRead[]>([])
  const [runningTasks, setRunningTasks] = useState<NotificationLogRead[]>([])
  const [selectedLog, setSelectedLog] = useState<NotificationLogRead | null>(null)
  const [ngOnly, setNgOnly] = useState(false)
  // 「チェックが外れているドメインを隠す」方式。初期状態は空 = 全表示なので、
  // 1 タップ外すだけで「Abema 以外」のようなフィルタが作れる。
  const hiddenDomains = useNotificationFilterStore((s) => s.hiddenDomains)
  const setHiddenDomains = useNotificationFilterStore((s) => s.setHiddenDomains)
  const excludedDomains = useMemo(() => new Set(hiddenDomains), [hiddenDomains])
  const setExcludedDomains = useCallback((next: Set<string>) => setHiddenDomains([...next]), [setHiddenDomains])
  const [filterOpen, setFilterOpen] = useState(false)
  // ドメイン選択肢はサマリー API + 取得済みログを union で蓄積し、
  // 一度でも観測したドメインは選択肢に残るようにする。
  const [availableDomains, setAvailableDomains] = useState<string[]>([])
  const [sortKey, setSortKey] = useState<'domain' | 'title' | 'success_count' | 'failed_count' | 'created_at' | null>(
    null,
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'created_at' ? 'desc' : 'asc')
    }
  }

  const filteredLogs = useMemo(() => {
    // ドメイン / NG フィルタはサーバ側で適用済み。ここではソートのみ。
    if (!sortKey) return logs
    return [...logs].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      const cmp = typeof av === 'string' ? av.localeCompare(bv as string) : (av as number) - (bv as number)
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [logs, sortKey, sortDir])
  const ngStreaks = useMemo(() => calcConsecutiveNgDays(ngStreaksLogs), [ngStreaksLogs])

  const mergeAvailableDomains = useCallback((domains: string[]) => {
    setAvailableDomains((prev) => {
      const merged = new Set(prev)
      for (const d of domains) merged.add(d)
      if (merged.size === prev.length) return prev
      return [...merged].sort()
    })
  }, [])

  // 選択肢は取得済みログだけだと欠けるため、サマリー API で全ドメインを先に揃える。
  useEffect(() => {
    getNotificationSummary()
      .then((res) => mergeAvailableDomains(res.domains.map((d) => d.domain)))
      .catch(() => undefined)
  }, [mergeAvailableDomains])

  // 除外が少数なら exclude_domain、選択が少数なら domain を送る。
  // どちらも「未観測のドメインをどう扱うか」が意図どおりになるように選び分けている。
  const domainParams = useMemo(() => {
    if (excludedDomains.size === 0) return {}
    const included = availableDomains.filter((d) => !excludedDomains.has(d))
    if (included.length > 0 && included.length <= excludedDomains.size) return { domain: included }
    return { exclude_domain: [...excludedDomains] }
  }, [excludedDomains, availableDomains])

  const fetchData = useCallback(
    async (signal?: AbortSignal) => {
      try {
        // フィルタが一切かかっていない呼び出しでは streak 用データも同時更新する。
        const isUnfiltered = excludedDomains.size === 0 && !ngOnly
        const res = await getNotifications(
          {
            ...domainParams,
            has_errors: ngOnly || undefined,
            limit: 200,
          },
          signal,
        )
        if (signal?.aborted) return
        setLogs(res.items)
        if (isUnfiltered) setNgStreaksLogs(res.items)
        mergeAvailableDomains(res.items.map((item) => item.domain))
      } catch (e) {
        if (axios.isCancel(e)) return
        console.error('通知ログ取得失敗:', e)
      }
    },
    [ngOnly, excludedDomains, domainParams, mergeAvailableDomains],
  )
  useEffect(() => {
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [fetchData])

  // 実行中タスクを取得（10秒ポーリング）
  const fetchRunningTasks = useCallback(async () => {
    try {
      const tasks = await getRunningNotifications()
      setRunningTasks(tasks)
    } catch {
      setRunningTasks([])
    }
  }, [])
  useEffect(() => {
    fetchRunningTasks()
    const id = setInterval(fetchRunningTasks, 10000)
    return () => clearInterval(id)
  }, [fetchRunningTasks])

  // NG連続中のドメイン一覧
  const activeStreaks = Object.entries(ngStreaks)
    .filter(([, days]) => days > 0)
    .sort((a, b) => b[1] - a[1])

  const domainFilterLabel = useMemo(() => {
    if (excludedDomains.size === 0) return 'すべてのドメイン'
    const included = availableDomains.filter((d) => !excludedDomains.has(d))
    if (included.length === 0) return 'ドメインなし'
    if (included.length === 1) return `${getDomainLabel(included[0])} のみ`
    if (excludedDomains.size === 1) return `${getDomainLabel([...excludedDomains][0])} 以外`
    return `${included.length} / ${availableDomains.length} ドメイン`
  }, [excludedDomains, availableDomains])

  return (
    <div className="flex-1 overflow-hidden flex flex-col">
      {/* 実行中タスクバナー */}
      {runningTasks.length > 0 && (
        <div className="mx-4 mt-3 mb-1 rounded-lg border border-blue-500/30 bg-blue-500/10 p-3">
          <p className="text-xs font-medium text-blue-400 mb-2 flex items-center gap-1.5">
            <Loader2 className="size-3.5 animate-spin" />
            実行中のタスク ({runningTasks.length})
          </p>
          <div className="flex flex-col gap-1">
            {runningTasks.map((task) => (
              <div key={task.id} className="flex items-center gap-2 text-xs">
                <Badge variant="outline" className={`text-[10px] shrink-0 ${getDomainColor(task.domain)}`}>
                  {getDomainLabel(task.domain)}
                </Badge>
                <span className="truncate text-muted-foreground">{task.title}</span>
                {task.started_at && (
                  <span className="shrink-0 text-blue-400/70 text-[10px]">{formatDate(task.started_at)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NG連続日数バナー */}
      {activeStreaks.length > 0 && (
        <div className="flex gap-2 px-4 pt-3 pb-1 overflow-x-auto">
          {activeStreaks.map(([domain, days]) => (
            <Badge key={domain} variant="destructive" className="whitespace-nowrap text-xs shrink-0">
              {getDomainLabel(domain)} {days}日連続NG
            </Badge>
          ))}
        </div>
      )}

      {/* フィルター切り替え */}
      <div className="flex items-center gap-2 px-4 py-2">
        <Button
          variant={ngOnly ? 'outline' : 'default'}
          size="sm"
          className="text-xs h-7"
          onClick={() => setNgOnly(false)}
        >
          すべて
        </Button>
        <Button
          variant={ngOnly ? 'default' : 'outline'}
          size="sm"
          className="text-xs h-7"
          onClick={() => setNgOnly(true)}
        >
          NGのみ
        </Button>

        <Button
          variant={excludedDomains.size > 0 ? 'default' : 'outline'}
          size="sm"
          className="ml-auto text-xs h-7 max-w-45"
          onClick={() => setFilterOpen(true)}
        >
          <SlidersHorizontal className="size-3.5 shrink-0" />
          <span className="truncate">{domainFilterLabel}</span>
        </Button>
      </div>

      {/* 除外中のドメイン */}
      {excludedDomains.size > 0 && (
        <div className="flex items-center gap-1.5 px-4 pb-2 overflow-x-auto">
          <span className="shrink-0 text-[11px] text-muted-foreground">非表示:</span>
          {[...excludedDomains].sort().map((d) => (
            <Badge
              key={d}
              variant="outline"
              className="shrink-0 cursor-pointer text-xs text-muted-foreground line-through"
              onClick={() => setHiddenDomains(hiddenDomains.filter((x) => x !== d))}
            >
              {getDomainLabel(d)}
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 text-xs h-6 text-muted-foreground"
            onClick={() => setHiddenDomains([])}
          >
            解除
          </Button>
        </div>
      )}

      <DomainFilterSheet
        open={filterOpen}
        onOpenChange={setFilterOpen}
        domains={availableDomains}
        excluded={excludedDomains}
        onChange={setExcludedDomains}
      />

      {filteredLogs.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">{ngOnly ? 'NGログなし' : 'ログなし'}</p>
      ) : (
        <div className="overflow-auto flex-1 **:data-[slot=table-container]:overflow-visible">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead className="w-20 cursor-pointer select-none" onClick={() => toggleSort('domain')}>
                  ドメイン
                  <SortIcon active={sortKey === 'domain'} direction={sortDir} />
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('title')}>
                  タイトル
                  <SortIcon active={sortKey === 'title'} direction={sortDir} />
                </TableHead>
                <TableHead
                  className="w-12 text-center cursor-pointer select-none"
                  onClick={() => toggleSort('success_count')}
                >
                  OK
                  <SortIcon active={sortKey === 'success_count'} direction={sortDir} />
                </TableHead>
                <TableHead
                  className="w-12 text-center cursor-pointer select-none"
                  onClick={() => toggleSort('failed_count')}
                >
                  NG
                  <SortIcon active={sortKey === 'failed_count'} direction={sortDir} />
                </TableHead>
                <TableHead className="w-28 cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                  日時
                  <SortIcon active={sortKey === 'created_at'} direction={sortDir} />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id} className="cursor-pointer" onClick={() => setSelectedLog(log)}>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${getDomainColor(log.domain)}`}>
                      {getDomainLabel(log.domain)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm truncate max-w-40">{log.title}</TableCell>
                  <TableCell className="text-center text-xs text-green-500">{log.success_count}</TableCell>
                  <TableCell
                    className={`text-center text-xs ${log.failed_count > 0 ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}
                  >
                    {log.failed_count}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDate(log.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* ログ詳細シート */}
      <Sheet open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <SheetContent side="right" className="w-[85%] sm:max-w-md p-0 flex flex-col">
          {selectedLog && (
            <>
              <SheetHeader className="px-4 pt-4 pb-2 border-b border-border space-y-0">
                <SheetTitle className="text-sm truncate pr-6">{selectedLog.title}</SheetTitle>
                <SheetDescription className="text-xs">
                  {parseUtcDate(selectedLog.created_at).toLocaleString('ja-JP')} |{' '}
                  <span className="text-green-500">成功: {selectedLog.success_count}</span>
                  {' / '}
                  <span className={selectedLog.failed_count > 0 ? 'text-red-500' : ''}>
                    失敗: {selectedLog.failed_count}
                  </span>
                </SheetDescription>
              </SheetHeader>
              <pre className="flex-1 overflow-auto p-4 text-xs whitespace-pre-wrap wrap-break-word text-muted-foreground">
                {selectedLog.message}
              </pre>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
